import { useInputControl } from '@conform-to/react'
import React, { useId } from 'react'
import PhoneInput from 'react-phone-number-input'
import { cn } from '#app/utils/misc.tsx'
import { Checkbox, type CheckboxProps } from './ui/checkbox.tsx'
import { DatePicker } from './ui/date-picker.tsx'
import { Input } from './ui/input.tsx'
import { Label } from './ui/label.tsx'
import {
	Select,
	SelectContent,
	SelectTrigger,
	SelectValue,
	type SelectProps,
	type SelectTriggerProps,
} from './ui/select.tsx'
import { Textarea } from './ui/textarea.tsx'
import 'react-phone-number-input/style.css'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function ErrorList({
	id,
	errors,
}: {
	errors?: ListOfErrors
	id?: string
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul
			id={id}
			className={cn({
				'flex flex-col gap-1': errorsToRender.length > 1,
			})}
		>
			{errorsToRender.map(e => (
				<li key={e} className="text-[10px] text-foreground-destructive">
					{e}
				</li>
			))}
		</ul>
	)
}

export function Field({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={cn('space-y-1', className, { 'pb-3': !errorId })}>
			<Label htmlFor={id} {...labelProps} />
			<Input
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
			/>
			{errorId ? (
				<div className="min-h-[32px] px-4 pb-3 pt-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={cn('space-y-1', className, { 'pb-3': !errorId })}>
			<Label htmlFor={id} {...labelProps} />
			<Textarea
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...textareaProps}
			/>
			{errorId ? (
				<div className="min-h-[32px] px-4 pb-3 pt-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function CheckboxField({
	labelProps,
	buttonProps,
	errors,
	className,
}: {
	labelProps: JSX.IntrinsicElements['label']
	buttonProps: CheckboxProps & {
		name: string
		form: string
		value?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const { key, defaultChecked, ...checkboxProps } = buttonProps
	const fallbackId = useId()
	const checkedValue = buttonProps.value ?? 'on'
	const input = useInputControl({
		key,
		name: buttonProps.name,
		formId: buttonProps.form,
		initialValue: defaultChecked ? checkedValue : undefined,
	})
	const id = buttonProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<div className="flex gap-2">
				<Checkbox
					{...checkboxProps}
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					checked={input.value === checkedValue}
					onCheckedChange={state => {
						input.change(state.valueOf() ? checkedValue : '')
						buttonProps.onCheckedChange?.(state)
					}}
					onFocus={event => {
						input.focus()
						buttonProps.onFocus?.(event)
					}}
					onBlur={event => {
						input.blur()
						buttonProps.onBlur?.(event)
					}}
					type="button"
				/>
				<label
					htmlFor={id}
					{...labelProps}
					className="self-center text-body-xs text-muted-foreground"
				/>
			</div>
			<div className="px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function SelectField({
	labelProps,
	selectProps,
	errors,
	className,
	children,
}: {
	children?: React.ReactNode
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	selectProps: SelectProps & {
		id?: string
		name: string
		form: string
		value?: string
		placeholder?: string
		triggerProps?: SelectTriggerProps
	}
	errors?: ListOfErrors
	className?: string | undefined
}) {
	const {
		defaultValue,
		value,
		id: selectId,
		placeholder,
		triggerProps,
		...inputProps
	} = selectProps
	const triggerClassname = triggerProps?.className
	const fallbackId = useId()
	const id = selectId ?? fallbackId
	const input = useInputControl({
		name: selectProps.name,
		formId: selectProps.form,
		initialValue: defaultValue ? defaultValue : undefined,
	})
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={cn('space-y-1', className, { 'pb-3': !errorId })}>
			<Label htmlFor={id} {...labelProps} />
			<Select
				key={`select-${id}`}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				defaultValue={defaultValue}
				value={input.value}
				onValueChange={value => input.change(value)}
				{...inputProps}
			>
				<SelectTrigger
					className={cn(
						'rounded-none border-0 border-b-2 border-tertiary md:w-60',
						triggerClassname,
					)}
					{...triggerProps}
				>
					<SelectValue placeholder={placeholder || 'Selecciona...'} />
				</SelectTrigger>
				<SelectContent>{children}</SelectContent>
			</Select>
			{errorId ? (
				<div className="min-h-[32px] px-4 pb-3 pt-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function DateField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement> & {
		name: string
		form: string
		value?: string
		defaulValue?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	const input = useInputControl({
		name: inputProps.name,
		formId: inputProps.form,
		initialValue: inputProps.defaultValue
			? (inputProps.defaultValue as string)
			: undefined,
	})
	return (
		<div
			className={cn('flex flex-col space-y-1', className, { 'pb-3': !errorId })}
		>
			<Label htmlFor={id} {...labelProps} />
			<DatePicker
				date={input.value ? new Date(input.value as string) : undefined}
				onDateChange={date => {
					input.change(date.toISOString())
				}}
			/>
			{errorId ? (
				<div className="min-h-[32px] px-4 pb-3 pt-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function PhoneNumberField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement> & {
		name: string
		form: string
		value?: string
		defaulValue?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	const input = useInputControl({
		name: inputProps.name,
		formId: inputProps.form,
		initialValue: inputProps.defaultValue
			? (inputProps.defaultValue as string)
			: undefined,
	})

	return (
		<div className={cn('space-y-1', className, { 'pb-3': !errorId })}>
			<Label htmlFor={id} {...labelProps} />
			<PhoneInput
				value={input.value}
				onChange={input.change}
				onBlur={input.blur}
				onFocus={input.focus}
				className="text-sm file:text-sm flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 ring-offset-background file:border-0 file:bg-transparent file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid"
				numberInputProps={{
					className: 'w-full bg-transparent',
				}}
				international={false}
				addInternationalOption={false}
				defaultCountry="PE"
				countryCallingCodeEditable={false}
				countryOptionsOrder={[
					'PE',
					'US',
					'AR',
					'CO',
					'CL',
					'CR',
					'MX',
					'UY',
					'VE',
				]}
			/>
			{errorId ? (
				<div className="min-h-[32px] px-4 pb-3 pt-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}
